"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../../constants/config";
import Pagination from "../../Common/Pagination";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const getName = (user) => {
  const first = user?.firstName || "";
  const last = user?.lastName || "";
  return `${first} ${last}`.trim() || user?.fullName || "Unnamed";
};

const getShiftId = (user) => {
  if (!user?.workShift) return "";
  return typeof user.workShift === "object" ? user.workShift._id || "" : user.workShift;
};

const getShiftName = (user, shiftMap) => {
  if (!user?.workShift) return "Not assigned";
  if (typeof user.workShift === "object") return user.workShift.shiftName || "Unknown";
  return shiftMap.get(user.workShift) || "Unknown";
};

export function WorkShiftUserMapping() {
  const [shifts, setShifts] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedShiftId, setSelectedShiftId] = useState("");
  const [selectedRole, setSelectedRole] = useState("all");
  const [query, setQuery] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

      const [shiftsRes, teachersRes, staffRes] = await Promise.all([
        axiosInstance.get(API_ENDPOINTS.workShifts, { headers }),
        axiosInstance.get(API_ENDPOINTS.usersTeachers, {
          headers,
          params: { status: "all" },
        }),
        axiosInstance.get(API_ENDPOINTS.usersStaff, {
          headers,
          params: { status: "all" },
        }),
      ]);

      setShifts(shiftsRes.data?.data?.shifts || []);
      setTeachers((teachersRes.data?.data?.teachers || []).filter((u) => u.role === "teacher"));
      setStaff((staffRes.data?.data?.staff || []).filter((u) => u.role === "staff"));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load shift mapping data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(""), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  const shiftMap = useMemo(() => new Map(shifts.map((s) => [s._id, s.shiftName])), [shifts]);

  const users = useMemo(() => {
    const merged = [
      ...teachers.map((u) => ({ ...u, roleLabel: "Teacher" })),
      ...staff.map((u) => ({ ...u, roleLabel: "Staff" })),
    ];

    return merged.filter((user) => {
      const byRole = selectedRole === "all" || user.role === selectedRole;
      const byQuery =
        query.trim() === "" ||
        getName(user).toLowerCase().includes(query.toLowerCase()) ||
        (user.email || "").toLowerCase().includes(query.toLowerCase());
      return byRole && byQuery;
    });
  }, [teachers, staff, selectedRole, query]);

  useEffect(() => {
    setCurrentPage(1);
  }, [query, selectedRole]);

  const paginatedUsers = users.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const visibleUserIds = paginatedUsers.map((u) => u._id);
  const allVisibleSelected = visibleUserIds.length > 0 && visibleUserIds.every((id) => selectedUserIds.includes(id));

  const toggleUser = (id) => {
    setSelectedUserIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleAllVisible = () => {
    setSelectedUserIds((prev) => {
      if (allVisibleSelected) {
        return prev.filter((id) => !visibleUserIds.includes(id));
      }
      const next = new Set([...prev, ...visibleUserIds]);
      return Array.from(next);
    });
  };

  const handleAssign = async () => {
    if (!selectedShiftId) {
      setError("Please select a shift first");
      return;
    }
    if (selectedUserIds.length === 0) {
      setError("Please select at least one teacher or staff member");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

      await Promise.all(
        selectedUserIds.map((userId) =>
          axiosInstance.put(
            API_ENDPOINTS.user(userId),
            { workShift: selectedShiftId },
            { headers }
          )
        )
      );

      const selectedShiftName = shiftMap.get(selectedShiftId) || "selected shift";
      setSuccess(`Mapped ${selectedUserIds.length} user(s) to ${selectedShiftName}`);
      setSelectedUserIds([]);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to map selected users");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="w-full">
      <CardContent className="p-4 sm:p-6 space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold">
              Teacher/Staff Shift Mapping
            </h2>
            <p className="text-xs sm:text-sm text-gray-600">
              Select users and map them to a work shift.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={loading || saving}
            className="w-full sm:w-auto"
          >
            Refresh
          </Button>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="md:col-span-2">
            <Label htmlFor="shift">Work Shift</Label>
            <select
              id="shift"
              className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={selectedShiftId}
              onChange={(e) => setSelectedShiftId(e.target.value)}
              disabled={loading || saving}
            >
              <option value="">Select shift</option>
              {shifts.map((shift) => (
                <option key={shift._id} value={shift._id}>
                  {shift.shiftName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="role">User Type</Label>
            <select
              id="role"
              className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={selectedRole}
              onChange={(e) => {
                setSelectedRole(e.target.value);
                setSelectedUserIds([]);
              }}
              disabled={loading || saving}
            >
              <option value="all">All</option>
              <option value="teacher">Teacher</option>
              <option value="staff">Staff</option>
            </select>
          </div>

          <div>
            <Label htmlFor="search">Search</Label>
            <Input
              id="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name or email"
              disabled={loading || saving}
            />
          </div>
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <label className="inline-flex items-center gap-2 text-xs sm:text-sm text-gray-700">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={toggleAllVisible}
              disabled={loading || saving || visibleUserIds.length === 0}
            />
            Select all visible users ({visibleUserIds.length})
          </label>
          <Button
            onClick={handleAssign}
            disabled={loading || saving || selectedUserIds.length === 0 || !selectedShiftId}
            className="w-full sm:w-auto"
          >
            {saving ? "Mapping..." : `Map Selected (${selectedUserIds.length})`}
          </Button>
        </div>

        {/* Desktop / large tablet table */}
        <div className="hidden md:block">
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">Select</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Current Shift</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedUsers.map((user) => (
                  <TableRow key={user._id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(user._id)}
                        onChange={() => toggleUser(user._id)}
                        disabled={loading || saving}
                      />
                    </TableCell>
                    <TableCell>{getName(user)}</TableCell>
                    <TableCell>{user.roleLabel}</TableCell>
                    <TableCell>{user.email || "-"}</TableCell>
                    <TableCell>{getShiftName(user, shiftMap)}</TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                      {loading ? "Loading users..." : "No users found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Mobile list view */}
        <div className="space-y-3 md:hidden">
          {users.length === 0 && (
            <div className="rounded-md border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
              {loading ? "Loading users..." : "No users found"}
            </div>
          )}
          {paginatedUsers.map((user) => {
            const checked = selectedUserIds.includes(user._id);
            return (
              <button
                key={user._id}
                type="button"
                onClick={() => toggleUser(user._id)}
                className={`w-full rounded-lg border px-4 py-3 text-left shadow-sm transition-colors ${
                  checked ? "border-blue-500 bg-blue-50" : "border-gray-200 bg-white hover:bg-gray-50"
                }`}
                disabled={loading || saving}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-900">
                      {getName(user)}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {user.roleLabel} • {getShiftName(user, shiftMap)}
                    </p>
                    <p className="mt-1 truncate text-xs text-gray-500">
                      {user.email || "No email"}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-gray-300"
                    checked={checked}
                    readOnly
                  />
                </div>
              </button>
            );
          })}
        </div>

        <Pagination
          currentPage={currentPage}
          totalItems={users.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
          currentPageCount={paginatedUsers.length}
          itemLabel="users"
        />
      </CardContent>
    </Card>
  );
}
